/**
 * Cognito Error Handling Utility
 *
 * Provides user-friendly error messages for AWS Cognito authentication exceptions.
 * Security consideration: UserNotFoundException and NotAuthorizedException
 * intentionally show the same message to prevent user enumeration attacks.
 */

export type AuthErrorContext = 'login' | 'mfa'

interface CognitoError extends Error {
  code?: string
  name: string
  message: string
}

function isCognitoError(error: unknown): error is CognitoError {
  return (
    error !== null &&
    typeof error === 'object' &&
    ('code' in error || 'name' in error)
  )
}

const LOGIN_ERROR_MESSAGES: Record<string, string> = {
  // Security: Same message for both to prevent user enumeration
  NotAuthorizedException: 'Incorrect email or password. Please try again.',
  UserNotFoundException: 'Incorrect email or password. Please try again.',

  // Account status issues
  UserNotConfirmedException:
    'Your email address has not been verified. Please check your email for a verification link.',
  PasswordResetRequiredException:
    'You must reset your password before signing in. Please check your email for reset instructions.',

  // Rate limiting
  TooManyRequestsException:
    'Too many sign-in attempts. Please wait a few minutes and try again.',
  LimitExceededException:
    'Too many failed attempts. Please wait a few minutes and try again.',

  // General errors
  InvalidParameterException:
    'Please check your email and password format and try again.',
  MFAMethodNotFoundException:
    'Two-factor authentication is not properly configured. Please contact support.',
  InvalidPasswordException:
    'The password does not meet requirements. Please try a different password.',
  NetworkError:
    'Unable to connect. Please check your internet connection and try again.',
}

const MFA_ERROR_MESSAGES: Record<string, string> = {
  CodeMismatchException:
    'The verification code is incorrect. Please check the code and try again.',
  ExpiredCodeException:
    'This verification code has expired. Please request a new code.',
  TooManyRequestsException:
    'Too many verification attempts. Please wait a few minutes and try again.',
  LimitExceededException:
    'Too many failed attempts. Please wait a few minutes and try again.',
  InvalidParameterException: 'Please enter a valid 6-digit verification code.',
  NotAuthorizedException: 'Your session has expired. Please sign in again.',
  MFAMethodNotFoundException:
    'Two-factor authentication method not found. Please contact support.',
  NetworkError:
    'Unable to connect. Please check your internet connection and try again.',
}

const DEFAULT_ERROR_MESSAGE =
  'An unexpected error occurred. Please try again or contact support if the problem persists.'

/**
 * Get user-friendly error message for Cognito authentication errors
 */
export function getCognitoErrorMessage(
  error: unknown,
  context: AuthErrorContext = 'login'
): string {
  if (typeof error === 'string') {
    return error
  }

  let errorCode: string | undefined
  let errorMessage: string | undefined

  if (isCognitoError(error)) {
    errorCode = error.code || error.name
    errorMessage = error.message
  }

  if (!errorCode) {
    console.error('Unhandled auth error:', error)
    return DEFAULT_ERROR_MESSAGE
  }

  // Handle NotAuthorizedException with specific messages
  if (errorCode === 'NotAuthorizedException' && errorMessage) {
    if (errorMessage.toLowerCase().includes('user is disabled')) {
      return 'Your account has been disabled. Please contact support for assistance.'
    }
  }

  const messageMap =
    context === 'mfa' ? MFA_ERROR_MESSAGES : LOGIN_ERROR_MESSAGES
  const message = messageMap[errorCode]

  if (!message) {
    console.error(`Unknown Cognito error code: ${errorCode}`, error)
    return DEFAULT_ERROR_MESSAGE
  }

  return message
}

/**
 * Check if error is a rate limiting error
 */
export function isRateLimitError(error: unknown): boolean {
  if (!isCognitoError(error)) return false
  const code = error.code || error.name
  return (
    code === 'TooManyRequestsException' || code === 'LimitExceededException'
  )
}

/**
 * Check if error requires password reset
 */
export function isPasswordResetRequired(error: unknown): boolean {
  if (!isCognitoError(error)) return false
  const code = error.code || error.name
  return code === 'PasswordResetRequiredException'
}

/**
 * Check if error requires email verification
 */
export function isEmailVerificationRequired(error: unknown): boolean {
  if (!isCognitoError(error)) return false
  const code = error.code || error.name
  return code === 'UserNotConfirmedException'
}

/**
 * Check if MFA code is expired
 */
export function isMFACodeExpired(error: unknown): boolean {
  if (!isCognitoError(error)) return false
  const code = error.code || error.name
  return code === 'ExpiredCodeException'
}
